import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with timezone and UTC plugins
dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * DST-safe date utility functions for handling French timezone and daylight saving time issues
 * Provides specialized functions for DST period detection, mismatch analysis, and corrections
 */

/**
 * French timezone constant for DST calculations
 */
const FRENCH_TIMEZONE = 'Europe/Paris';

/**
 * Checks if a given date is in DST period for French timezone (UTC+2)
 * @param date - Date to check
 * @returns true if date is in DST period, false if in standard time (UTC+1)
 */
export function isDSTPeriod(date: Date): boolean {
  const parisTime = dayjs(date).tz(FRENCH_TIMEZONE);
  // DST in Europe/Paris is UTC+2 (offset of 120 minutes)
  // Standard time is UTC+1 (offset of 60 minutes)
  return Math.abs(parisTime.utcOffset()) === 120;
}

/**
 * Detects DST period mismatch between current date and target date
 * @param targetDate - Target event date
 * @param currentDate - Current date (defaults to now)
 * @returns Object with mismatch info and correction needed
 */
export function detectDSTPeriodMismatch(
  targetDate: Date,
  currentDate: Date = new Date()
): { hasMismatch: boolean; currentIsDST: boolean; targetIsDST: boolean; correctionHours: number } {
  const currentIsDST = isDSTPeriod(currentDate);
  const targetIsDST = isDSTPeriod(targetDate);

  const hasMismatch = currentIsDST !== targetIsDST;

  // Calculate correction needed (positive = add hours, negative = subtract hours)
  let correctionHours = 0;
  if (hasMismatch) {
    // If current date is DST (+2) but target is not (+1), we need to add 1 hour
    // If current date is standard (+1) but target is DST (+2), we need to subtract 1 hour
    correctionHours = currentIsDST ? 1 : -1;
  }

  return {
    hasMismatch,
    currentIsDST,
    targetIsDST,
    correctionHours,
  };
}

/**
 * Applies DST correction to a target date if needed
 * @param targetDate - Target date to correct
 * @param currentDate - Current date (defaults to now)
 * @returns Corrected date or original date if no correction needed
 */
export function applyDSTCorrection(targetDate: Date, currentDate: Date = new Date()): Date {
  const { hasMismatch, correctionHours } = detectDSTPeriodMismatch(currentDate, targetDate);

  if (!hasMismatch) {
    return targetDate;
  }

  return dayjs(targetDate).add(correctionHours, 'hour').toDate();
}

/**
 * Gets timezone offset information for a date in French timezone
 * @param date - Date to analyze
 * @returns Object with timezone information
 */
export function getTimezoneInfo(date: Date): {
  utcTime: string;
  parisTime: string;
  offset: string;
  isDST: boolean;
  offsetMinutes: number;
} {
  const utcTime = dayjs(date).utc();
  const parisTime = dayjs(date).tz(FRENCH_TIMEZONE);

  const offsetMinutes = parisTime.utcOffset();
  const isDST = Math.abs(offsetMinutes) === 120;

  return {
    utcTime: utcTime.format(),
    parisTime: parisTime.format(),
    offset: parisTime.format('Z'),
    isDST,
    offsetMinutes,
  };
}

/**
 * Parses a date string or Date object and treats it as Europe/Paris timezone
 * @param date - Date to parse
 * @returns dayjs object in Europe/Paris timezone
 */
export function parseDateInParis(date: string | Date): dayjs.Dayjs {
  if (typeof date === 'string') {
    return dayjs.tz(date, FRENCH_TIMEZONE);
  }
  return dayjs(date).tz(FRENCH_TIMEZONE);
}

/**
 * Formats a date for backend submission in Europe/Paris timezone
 * @param date - Date to format
 * @returns ISO string with Europe/Paris timezone
 */
export function formatDateForBackend(date: Date | dayjs.Dayjs): string {
  return dayjs(date).tz(FRENCH_TIMEZONE).format('YYYY-MM-DDTHH:mm:ssZ');
}

/**
 * Formats a date in Europe/Paris timezone with custom format
 * @param date - Date to format
 * @param format - Format string
 * @returns Formatted string
 */
export function formatDateInParis(date: Date | dayjs.Dayjs, format: string): string {
  return dayjs(date).tz(FRENCH_TIMEZONE).format(format);
}
